/**
 * `field-accuracy` — per-field extraction accuracy · CLM-107 (FR-30, DR-08;
 * NFR-16 design only). Refinements R-107-1 … R-107-9 are proposals made while
 * developing; D-2.1-1/2/3, which they lean on, are recommendations, not
 * ratified decisions.
 *
 * One read-only `GET /v1/admin/field-accuracy` behind the Admin **Extraction
 * accuracy** surface. For each `(field_name, extraction_schema_version,
 * extraction_model)` the pipeline has extracted, it answers: how often was the
 * model right, and how many human decisions is that figure resting on?
 *
 * ## What it deliberately does not answer
 *
 * **Whether a figure is good enough.** There is no threshold on the wire and
 * no pass/fail flag: the ticket separates measurement (this story) from
 * auto-approval (CLM-108) precisely so the threshold can be set from a
 * fortnight of real numbers. 0.900 is CLM-108's number, not this one's.
 *
 * ## The rate (R-107-4, R-107-8)
 *
 * `accuracy_rate = verified_count / (verified_count + corrected_count)`, rounded
 * to 3 dp, and `null` when that denominator is 0 — never 0, which would read as
 * "always wrong". `Rejected` means the reviewer could not settle the value, not
 * that the model was wrong, so it is its own count outside the denominator
 * (FR-30 verbatim).
 *
 * **The counts are exact and are the figure of record.** Anything that compares
 * against a threshold (CLM-108) must compare counts —
 * `verified × 1000 ≥ threshold × 1000 × sample_size` — never the rounded rate:
 * 0.8996 rounds to 0.900.
 *
 * ## The grain carries the model (R-107-1)
 *
 * D-2.1-1 recommends per `(field, schema_version)`; D-2.1-3 recommends that a
 * model change resets the sample. Only a grain with the model does both. One
 * row per field is `is_current` — the grain whose latest extraction is the most
 * recent — and older `(schema, model)` rows are history the page shows muted.
 *
 * ## Scope (R-107-5)
 *
 * The API restates `clm.vw_field_accuracy` with `toScopeSqlPredicate` on
 * `vw_contract_scope.effective_contract_key` inside each aggregate, so the
 * figures cover only contracts the caller may see (rules 9, 10). `scope_kind`
 * says which: a `practices` admin is looking at part of the corpus, and the
 * page says so rather than presenting a partial figure as the whole.
 *
 * ## Gate (R-107-6)
 *
 * `admin:configure` — Admin only; Legal does not hold it (`roles.ts`). FR-31
 * names that gate for the surface CLM-108 extends. Read-only, no audit row, no
 * `LIMIT`, no pagination (R-107-9): at rule-17 volume this is tens of rows.
 */
import { z } from 'zod';
import { FieldNameSchema } from '../domain/verification.js';
import { IsoTimestampUtcSchema } from '../primitives.js';
import { VERIFICATION_MINIMUM_SAMPLE } from '../vocabulary.js';
const Count = z.number().int().min(0);
/**
 * The sample size below which a rate is flagged as too thin to read (R-107-7).
 *
 * D-2.1-2, **decided 1 Oct 2026** (Harnoor Gandhi, Jira CLM-108 comment
 * 1942022): n = 30 settled instances ("at ~10 contracts/month a field reaches
 * 30 in a quarter"). An alias of {@link VERIFICATION_MINIMUM_SAMPLE}, the
 * number CLM-108's auto-approval decides on, so the two pages can never
 * disagree about what "enough decisions" means. Here it drives a display flag
 * only, `meets_minimum_sample`.
 */
export const FIELD_ACCURACY_MINIMUM_SAMPLE = VERIFICATION_MINIMUM_SAMPLE;
/** No parameters. `.strict()`, so a `scope` or `threshold` key is a 400, never ignored. */
export const FieldAccuracyRequestSchema = z.object({}).strict();
/**
 * One `(field_name, extraction_schema_version, extraction_model)` grain.
 *
 * The first twelve keys are `clm.vw_field_accuracy`'s columns under the
 * caller's scope; the last three are the handler's. The refinement pins the
 * arithmetic the page relies on, so a handler bug is a 500 at the API's own
 * response parse rather than a plausible wrong number on screen.
 */
export const FieldAccuracyRowSchema = z
    .object({
    field_name: FieldNameSchema,
    extraction_schema_version: z.string().min(1).max(20),
    /** As the provider reported it on the `AIExtraction` row (CR-01). */
    extraction_model: z.string().min(1).max(120),
    /** Pipeline `AIExtraction` rows in the grain, in scope. At least one — a grain exists because something was extracted. */
    extraction_count: z.number().int().min(1),
    verified_count: Count,
    corrected_count: Count,
    /** Counted, but outside the denominator (R-107-4). */
    rejected_count: Count,
    /** `verified_count + corrected_count` — the denominator. */
    sample_size: Count,
    /** Rounded to 3 dp; `null` at `sample_size = 0`. Display only — compare the counts (R-107-8). */
    accuracy_rate: z.number().min(0).max(1).nullable(),
    /** Span of the sample (Verified + Corrected decisions); `null` when there is none. */
    first_decision_utc: IsoTimestampUtcSchema.nullable(),
    /** The NFR-16 staleness input CLM-108 reads. `null` when the sample is empty. */
    last_decision_utc: IsoTimestampUtcSchema.nullable(),
    last_extraction_utc: IsoTimestampUtcSchema,
    /** `orchestrator/fields.ts` `HIGH_STAKES_FIELDS` — FR-07's SLA set. */
    is_high_stakes: z.boolean(),
    /** Exactly one row per `field_name`: the grain holding the field's most recent extraction. */
    is_current: z.boolean(),
    /** `sample_size >= minimum_sample` (R-107-7). A display flag; it decides nothing. */
    meets_minimum_sample: z.boolean(),
})
    .strict()
    .superRefine((row, ctx) => {
    if (row.sample_size !== row.verified_count + row.corrected_count) {
        ctx.addIssue({
            code: 'custom',
            path: ['sample_size'],
            message: 'sample_size must equal verified_count + corrected_count (Rejected is outside the denominator)',
        });
    }
    if ((row.accuracy_rate === null) !== (row.sample_size === 0)) {
        ctx.addIssue({
            code: 'custom',
            path: ['accuracy_rate'],
            message: 'accuracy_rate is null exactly when sample_size is 0 — never 0 for an empty sample',
        });
    }
    if ((row.last_decision_utc === null) !== (row.sample_size === 0)) {
        ctx.addIssue({
            code: 'custom',
            path: ['last_decision_utc'],
            message: 'the sample span is null exactly when sample_size is 0',
        });
    }
});
export const FieldAccuracyResponseSchema = z
    .object({
    generated_utc: IsoTimestampUtcSchema,
    /**
     * The caller's scope shape, never the practice list. `practices` means the
     * figures cover only part of the corpus, and the page says so (R-107-5).
     */
    scope_kind: z.enum(['all', 'practices']),
    /** {@link FIELD_ACCURACY_MINIMUM_SAMPLE} as the server applied it to `meets_minimum_sample`. */
    minimum_sample: z.number().int().min(1),
    /** Ordered by `field_name`, then most recent extraction first — so each field's current row leads its group. */
    rows: z.array(FieldAccuracyRowSchema),
})
    .strict()
    .superRefine((body, ctx) => {
    const current = new Map();
    for (const row of body.rows) {
        if (row.is_current)
            current.set(row.field_name, (current.get(row.field_name) ?? 0) + 1);
    }
    for (const field of new Set(body.rows.map((row) => row.field_name))) {
        if (current.get(field) !== 1) {
            ctx.addIssue({
                code: 'custom',
                path: ['rows'],
                message: `field ${field} must have exactly one is_current row`,
            });
        }
    }
    body.rows.forEach((row, index) => {
        if (row.meets_minimum_sample !== row.sample_size >= body.minimum_sample) {
            ctx.addIssue({
                code: 'custom',
                path: ['rows', index, 'meets_minimum_sample'],
                message: 'meets_minimum_sample must equal sample_size >= minimum_sample',
            });
        }
    });
});
//# sourceMappingURL=field-accuracy.js.map